/**
 * Privacy Service
 * 
 * Sanitize user program data before sharing with community
 * Removes all personally identifiable information
 * 
 * Philosophy: We're sharing public camp information, not user-specific data
 */

/**
 * Extract and sanitize public camp data from user program
 * @param {Object} programData - Raw program_data from user_programs table
 * @param {Object} userProfile - User's profile (for child names to redact)
 * @returns {Object} Sanitized data safe for community sharing
 */
export function sanitizeProgramDataForSharing(programData, userProfile = null) {
  // Child names to redact from any text fields
  const childNames = userProfile?.children?.map(c => c.name) || [];
  const userName = userProfile?.full_name || '';
  
  // Map camp_start_date/camp_end_date to schedule_dates format
  const scheduleDates = programData.schedule_dates ? {
    start: programData.schedule_dates.start,
    end: programData.schedule_dates.end
  } : (programData.camp_start_date || programData.campEndDate || programData.start_date) ? {
    start: programData.camp_start_date || programData.campStartDate || programData.start_date,
    end: programData.camp_end_date || programData.campEndDate || programData.end_date
  } : null;
  
  // Build schedule_text from session times if schedule_text is not available
  let scheduleText = programData.schedule_text;
  if (!scheduleText && (programData.session_start_time || programData.session_end_time)) {
    const parts = [];
    if (programData.session_start_time) parts.push(`Drop-off: ${programData.session_start_time}`);
    if (programData.session_end_time) parts.push(`Pick-up: ${programData.session_end_time}`);
    if (parts.length > 0) {
      scheduleText = parts.join(', ');
    }
  }
  
  return {
    // ✅ Core public information (camp's public data)
    name: programData.name?.trim(),
    organization: programData.organization?.trim(),
    description: sanitizeText(programData.description || programData.editorial_summary || programData.brief_description, childNames, userName),
    brief_description: sanitizeText(programData.description || programData.editorial_summary || programData.brief_description, childNames, userName),
    website: sanitizeUrl(programData.website || programData.website_url),
    phone: sanitizePhone(programData.phone || programData.contact_phone),
    email: programData.email || programData.contact_email || null,
    program_type: programData.program_type,
    
    // ✅ Location (camp's public address, not user's home)
    location: sanitizeLocation(programData.location || programData.formatted_address || programData.general_location),
    latitude: programData.latitude || programData.coordinates_lat,
    longitude: programData.longitude || programData.coordinates_lng,
    
    // ✅ Dates (camp session dates - public information)
    schedule_dates: scheduleDates,
    
    // ✅ Important dates
    registration_open_date: programData.registration_open_date || null,
    early_registration_open_date: programData.early_registration_open_date || null,
    registration_deadline: programData.registration_deadline || null,
    early_bird_deadline: programData.early_bird_deadline || null,
    cancellation_deadline: programData.cancellation_deadline || null,
    
    // ✅ Session times (direct fields)
    session_start_time: programData.session_start_time || null,
    session_end_time: programData.session_end_time || null,
    
    // ✅ Cost (publicly advertised pricing)
    price: programData.price ? parseFloat(programData.price) : null,
    price_period: programData.price_period || 'week',
    price_notes: sanitizeText(programData.price_notes, childNames, userName),
    
    // ⚠️ Schedule (sanitize text to remove names)
    schedule_text: sanitizeText(scheduleText, childNames, userName),
    
    // ✅ Activities (public program offerings)
    activities: programData.activities || [],
    
    // ✅ Age range (camp's advertised age range)
    age_range: programData.age_range,
    
    // ✅ Refund policy
    refund_policy: sanitizeText(programData.refund_policy, childNames, userName),
    
    // Metadata for tracking
    _sanitized_at: new Date().toISOString(),
    _source: 'user_program'
  };
}

/**
 * Sanitize location to city-level only (no street addresses)
 */
function sanitizeLocation(location) {
  if (!location) return null;
  
  // If it's already city-level (no commas or short), return as-is
  if (!location.includes(',') || location.length < 20) {
    return location.trim();
  }
  
  // Extract city, state from full address
  // "123 Main St, San Francisco, CA 94102" → "San Francisco, CA"
  const parts = location.split(',').map(p => p.trim());
  
  if (parts.length >= 2) {
    // Take last 2 parts (city, state)
    const city = parts[parts.length - 2];
    const state = parts[parts.length - 1].replace(/\d{5}(-\d{4})?/, '').trim(); // Remove zip
    return `${city}, ${state}`;
  }
  
  return location.trim();
}

/**
 * Sanitize text fields to remove PII
 */
function sanitizeText(text, childNames = [], userName = '') {
  if (!text) return null;
  
  let sanitized = text;
  
  // Remove child names (replace with generic placeholder)
  childNames.forEach(name => {
    if (!name) return;
    const regex = new RegExp(`\\b${escapeRegex(name)}\\b`, 'gi');
    sanitized = sanitized.replace(regex, '[child]');
  });
  
  // Remove user name
  if (userName) {
    const regex = new RegExp(`\\b${escapeRegex(userName)}\\b`, 'gi');
    sanitized = sanitized.replace(regex, '[parent]');
  }
  
  // Remove common PII patterns
  sanitized = sanitized
    // Phone numbers: (123) 456-7890, 123-456-7890, 123.456.7890
    .replace(/\b\d{3}[-.)]\s*\d{3}[-.)]\s*\d{4}\b/g, '[phone]')
    // Email addresses
    .replace(/\b[\w.%+-]+@[\w.-]+\.[A-Z]{2,}\b/gi, '[email]')
    // Credit card numbers (16 digits)
    .replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, '[card]')
    // SSN patterns
    .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[ssn]');
  
  return sanitized.trim();
}

/**
 * Sanitize URL (validate and clean)
 */
function sanitizeUrl(url) {
  if (!url) return null;
  
  url = url.trim();
  
  // Add https:// if missing protocol
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = 'https://' + url;
  }
  
  // Validate URL format
  try {
    new URL(url);
    return url;
  } catch {
    return null; // Invalid URL
  }
}

/**
 * Sanitize phone number
 */
function sanitizePhone(phone) {
  if (!phone) return null;
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Validate length (US phone numbers)
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  
  return phone; // Return original if can't parse
}

/**
 * Escape special regex characters
 */
function escapeRegex(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Validate that no PII leaked through sanitization
 */
export function validateNoPII(sanitizedData, userProfile) {
  const issues = [];
  
  const childNames = userProfile?.children?.map(c => c.name).filter(Boolean) || [];
  const userName = userProfile?.full_name || '';
  
  // Collect all text fields to check
  const textFields = [
    sanitizedData.name,
    sanitizedData.organization,
    sanitizedData.location,
    sanitizedData.schedule_text,
    sanitizedData.price_notes
  ].filter(Boolean);
  
  textFields.forEach(text => {
    const lowerText = text.toLowerCase();
    
    // Check for child names
    childNames.forEach(name => {
      if (name && lowerText.includes(name.toLowerCase())) {
        issues.push(`Contains child name: "${name}"`);
      }
    });
    
    // Check for user name
    if (userName && lowerText.includes(userName.toLowerCase())) {
      issues.push(`Contains user name: "${userName}"`);
    }
    
    // Check for email pattern
    if (/@[\w.-]+\.[A-Z]{2,}/i.test(text)) {
      issues.push('Contains email address');
    }
    
    // Check for phone pattern (digits only)
    const phonePattern = /\b\d{3}[-.)]\s*\d{3}[-.)]\s*\d{4}\b/;
    if (phonePattern.test(text)) {
      issues.push('Contains phone number in text');
    }
  });
  
  return {
    safe: issues.length === 0,
    issues,
    severity: issues.length > 0 ? 'high' : 'none'
  };
}

/**
 * Parse schedule text into structured data
 */
export function parseScheduleText(scheduleText) {
  if (!scheduleText) return null;
  
  const parsed = {
    raw_text: scheduleText,
    drop_off: null,
    pick_up: null,
    meals: [],
    activities_mentioned: []
  };
  
  // Extract times (e.g., "9am-3pm", "9:00 AM - 3:00 PM")
  const timePattern = /(\d{1,2}):?(\d{2})?\s*(am|pm)?/gi;
  const times = scheduleText.match(timePattern);
  
  if (times && times.length >= 2) {
    parsed.drop_off = times[0];
    parsed.pick_up = times[times.length - 1];
  }
  
  // Extract meal mentions
  const mealKeywords = ['lunch', 'breakfast', 'snack', 'dinner', 'meal'];
  mealKeywords.forEach(meal => {
    if (scheduleText.toLowerCase().includes(meal)) {
      parsed.meals.push(meal);
    }
  });
  
  return parsed;
}

